import { isEmpty, some } from 'lodash'
import { TypedUseSelectorHook, useSelector } from 'react-redux'

import { selectGraduationYear } from './user'
import { useCanRequestRecommendations } from './user/hooks'
import { CommonAppRestrictionReason, UserState } from './user/type-definitions'

type RootState = {
  user: UserState
  parents: { records?: unknown[] }
}

export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector

export const selectMatchCategory = (store: RootState) => {
  return store.user.record?.currentMatchLevel
}

export const useCommonAppConnectionRestrictions = () => {
  const user = useAppSelector((store) => store.user.record)
  const commonAppRestrictions: Record<CommonAppRestrictionReason, boolean> = {
    'counselor-not-assigned': false,
    'invalid-class-year': false,
    shutdown: false,
    'not-classified': false,
  }
  user?.commonAppConnectRestrictions?.forEach((reason) => {
    commonAppRestrictions[reason] = true
  })
  return commonAppRestrictions
}

export const useCollegeMatchCategory = () => {
  return useAppSelector(selectMatchCategory)
}

export const selectHasManualMatchLevel = (store: RootState): boolean => {
  return store.user.record?.currentMatchLevelType === 'manual'
}

export const selectCanSeeBalancedLists = (store: RootState): boolean => {
  return store.user.record?.userCanSeeBalancedList ?? false
}

export const useCanSeeBalancedLists = (): boolean => {
  return useAppSelector(selectCanSeeBalancedLists)
}
export const selectPreferences = (store: RootState): Record<string, boolean> => store.user.record?.Preferences ?? {}
export const useSelectPreference = (prefKey: string): boolean => useAppSelector(selectPreferences)?.[prefKey] ?? false

const getHasPreference = (prefArr?: Record<string, string[]>) => some(prefArr, (el) => !isEmpty(el))
export const hasAnySavedCollegePreferenes = (store: RootState): boolean =>
  getHasPreference(store?.user?.record?.MustHave) || getHasPreference(store?.user?.record?.NiceToHave)
export const useHasAnySavedCollegePreferenes = () => useAppSelector(hasAnySavedCollegePreferenes)

export const getPersonalStatement = (store: RootState) => store?.user?.record?.Profile?.PersonalStatement
export const useHasPersonalStatement = () => !!useAppSelector(hasAnySavedCollegePreferenes)

export const selectProfile = (store: RootState) => store.user.record?.Profile
export const useSelectProfile = () => useAppSelector(selectProfile)

export const selectParents = (store: RootState) => store.parents?.records ?? []
export const useSelectParents = () => useAppSelector(selectParents)

export const useCanSeeAcceptanceLikelihood = (store: RootState) => {
  return store.user.record?.userCanSeeAcceptanceLikelihood || false
}

export const selectIsCommonAppIntegrated = (store: RootState) => {
  return store.user.record?.HighSchool?.productConfig.highSchoolConfig.commonAppIntegrationEnabled.enabled || false
}
export const selectSchoolOrgRecommendationRanking = (store: RootState) =>
  store.user.record?.HighSchool?.productConfig?.highSchoolConfig?.enhancedRecommendationAssignment
export const useIsSchoolOrgRecommendationRankingEnabled = (): boolean => {
  const gradYear = useAppSelector(selectGraduationYear)
  const enhancedRecommendationAssignment = useAppSelector(selectSchoolOrgRecommendationRanking)
  return !!(
    enhancedRecommendationAssignment?.startYear &&
    enhancedRecommendationAssignment?.enabled &&
    gradYear >= enhancedRecommendationAssignment?.startYear
  )
}
export const useCanStudentAssignRecommendations = (): boolean => {
  const isRankingEnabled = useIsSchoolOrgRecommendationRankingEnabled()
  const canRequestRecommendations = useCanRequestRecommendations()
  return !!(isRankingEnabled && canRequestRecommendations)
}
export const useCommonAppIntegrated = (): boolean => useAppSelector(selectIsCommonAppIntegrated) ?? false

export const useIsCommonAppProfileConnected = (): boolean =>
  useAppSelector((store) => store?.user?.record?.Profile?.commonApp?.connected) ?? false

export const useCanDiscoverUsingAcceptanceLikelihood = (): boolean => {
  const hasManualMatchLevel = useAppSelector(selectHasManualMatchLevel)
  return hasManualMatchLevel
}

export const useCanSeePredictiveChances = (store: RootState) => {
  return store.user.record?.canSeePredictiveChances || false
}
// This field is intended to give more context to why useCanSeePredictiveChances might be false.
// It should NOT be used to control whether a student is allowed to see predictive chances by itself.
// Use in conjunction with useCanSeePredictiveChances.
export const useIsPredictiveChancesEnabledForHS = (store: RootState) => {
  return store.user.record?.HighSchool?.displayPredictiveChances || false
}

export const useCanSeeScattergrams = (store: RootState) => {
  return store.user.record?.canSeeScattergrams || false
}
// This field is intended to give more context to why useCanSeeScattergrams might be false.
// It should NOT be used to control whether a student is allowed to see scattergrams by itself.
// Use in conjunction with useCanSeeScattergrams.
export const useIsScattergramsEnabledForHS = (store: RootState) => {
  return store.user.record?.HighSchool?.displayScattergrams || false
}

export const useStateOfResidence = (store: RootState) => {
  return store.user.record?.Profile?.Address?.State || store.user.record?.HighSchool?.State
}
